import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import actions from "../../redux/admin/actions";
import withRouter from "../../hoc/withRouter";
import { formatAdminDate, formatAdminDateTime } from "../../dates";
import { organizationPath } from "../../organizationPath";
import { countryOptions, timezoneOptions } from "../../locations";
import { showAdminFeedback } from "../../feedback";
import Wrapper from "./index.style";

const STATUS_COLORS = {
  trial: "blue", pending_invoice: "orange", active: "green", suspended: "red", cancelled: "default",
};
const FEATURE_KEYS = ["dashboards", "csv", "insights", "export", "embed", "bellwethers", "electoral_law", "intelligence", "white_label", "redistribution"];
const PLAN_SEATS = {basic: 3, team: 10, enterprise: 25};
const GRANT_KEYS = ["historical", "nowcast_full", "intelligence", "intelligence_live", "intelligence_export", "intelligence_audit", "api", "broadcast"];
const {
  fetchOrganizations,
  createOrganization,
  updateOrganization,
  inviteOrganization,
  issueInvoice,
  clearFeedback,
} = actions;

export class OrganizationsPanel extends Component {
  state = {
    editing: null,
    inviteOrg: null,
    invoiceOrg: null,
    selectedCountry: "GR",
    selectedPlan: "basic",
  };

  formRef = React.createRef();

  inviteFormRef = React.createRef();

  invoiceFormRef = React.createRef();

  componentDidMount() {
    this.props.fetchOrganizations({ per_page: 100 });
  }

  componentDidUpdate(prevProps) {
    const { feedback, clearFeedback, t } = this.props;
    if (!feedback || feedback === prevProps.feedback) return;

    showAdminFeedback(feedback, t);
    if (feedback.type !== "error") {
      if (["organizationCreated", "organizationUpdated"].includes(feedback.operation)) {
        this.setState({ editing: null });
      }
      if (feedback.operation === "invitationSent") {
        this.setState({ inviteOrg: null });
        this.inviteFormRef.current?.resetFields();
      }
      if (feedback.operation === "invoiceSent") {
        this.setState({ invoiceOrg: null });
        this.invoiceFormRef.current?.resetFields();
      }
    }
    clearFeedback();
  }

  openEditor = (organization) => {
    const editing = organization || {};
    const selectedCountry = organization?.country_code || "GR";
    this.setState({ editing, selectedCountry, selectedPlan: organization?.billing_plan || (organization ? "custom" : "basic") }, () => {
      this.formRef.current?.setFieldsValue({
        name: organization?.name,
        legal_name: organization?.legal_name,
        tax_id: organization?.tax_id,
        billing_email: organization?.billing_email,
        locale: organization?.locale || "el",
        address: organization?.address,
        city: organization?.city,
        country_code: selectedCountry,
        timezone: organization?.timezone || "Europe/Athens",
        seat_limit: organization?.seat_limit || 3,
        billing_plan: organization?.billing_plan || (organization ? "custom" : "basic"),
        billing_status: organization?.billing_status || "pending_invoice",
        trial_ends_on: organization?.trial_ends_on,
        notes: organization?.notes,
        disabled_features: FEATURE_KEYS.filter(
          (key) => organization?.feature_flags && organization.feature_flags[key] === false,
        ),
        granted_features: GRANT_KEYS.filter(
          (key) => organization?.feature_flags && organization.feature_flags[key] === true,
        ),
      });
    });
  };

  saveOrganization = async () => {
    const values = await this.formRef.current.validateFields();
    const { disabled_features: disabled = [], granted_features: granted = [], ...fields } = values;
    const feature_flags = {};
    disabled.forEach((key) => { feature_flags[key] = false; });
    granted.forEach((key) => { feature_flags[key] = true; });
    const payload = { ...fields, feature_flags };
    const { editing } = this.state;

    if (editing?.id) this.props.updateOrganization(editing.id, payload, editing.name);
    else this.props.createOrganization(payload);
  };

  sendInvite = async () => {
    const invite = await this.inviteFormRef.current.validateFields();
    this.props.inviteOrganization(this.state.inviteOrg.id, invite, "list");
  };

  issueInvoice = async () => {
    const { amount_eur, description, due_days } = await this.invoiceFormRef.current.validateFields();
    this.props.issueInvoice(this.state.invoiceOrg.id, {
      amount_cents: Math.round(amount_eur * 100), description, due_days,
    });
  };

  handleCountryChange = (countryCode) => {
    const zones = timezoneOptions(countryCode);
    this.setState({ selectedCountry: countryCode });
    this.formRef.current?.setFieldValue("timezone", zones.length === 1 ? zones[0].value : undefined);
  };

  render() {
    const {
      t, i18n, rows, loading, error, mutationLoading, updateOrganization, router,
    } = this.props;
    const { editing, inviteOrg, invoiceOrg, selectedCountry, selectedPlan } = this.state;
    const grants = selectedPlan === "custom" ? GRANT_KEYS : GRANT_KEYS.filter(key => ["historical", "nowcast_full", "broadcast", ...(selectedPlan === "basic" ? [] : ["intelligence_live", "intelligence_export", "intelligence_audit"]), ...(selectedPlan === "team" ? ["api"] : [])].includes(key));
    const columns = [
      {
        title: t("admin.common.name"), dataIndex: "name",
        render: (name, organization) => (
          <Button type="link" className="table-link" onClick={() => router.navigate(organizationPath(organization))}>
            {name}
          </Button>
        ),
      },
      { title: t("admin.organizations.taxId"), dataIndex: "tax_id", width: 120 },
      {
        title: t("admin.organizations.billing"), dataIndex: "billing_status", width: 140,
        render: (status) => <Tag color={STATUS_COLORS[status]}>{t(`admin.common.statuses.${status}`)}</Tag>,
      },
      { title: t("admin.organizations.plan"), dataIndex: "billing_plan", render: plan => t(`admin.organizations.plans.${plan || "custom"}`) },
      { title: t("admin.organizations.seats"), width: 100, render: (organization) => `${organization.seats_taken}/${organization.seat_limit}` },
      {
        title: t("admin.organizations.createdAt"), dataIndex: "created_at", width: 170,
        render: (value, organization) => formatAdminDateTime(value, organization.timezone, i18n.language),
      },
      {
        title: t("admin.organizations.due"), dataIndex: "invoice_due_on", width: 120,
        render: (value) => formatAdminDate(value, i18n.language),
      },
      {
        title: t("admin.organizations.actions"), width: 340,
        render: (organization) => (
          <Space wrap>
            <Button size="small" onClick={() => this.openEditor(organization)}>{t("admin.organizations.edit")}</Button>
            <Button size="small" onClick={() => this.setState({ inviteOrg: organization })}>{t("admin.organizations.invite")}</Button>
            <Button size="small" onClick={() => this.setState({ invoiceOrg: organization })}>{t("admin.organizations.invoice")}</Button>
            {organization.billing_status !== "suspended" ? (
              <Popconfirm
                title={t("admin.organizations.confirmSuspend", { name: organization.name })}
                onConfirm={() => updateOrganization(organization.id, { billing_status: "suspended" }, organization.name)}
                okText={t("admin.common.confirm")}
                cancelText={t("admin.common.cancel")}
              >
                <Button size="small" danger>{t("admin.organizations.suspend")}</Button>
              </Popconfirm>
            ) : (
              <Button size="small" onClick={() => updateOrganization(organization.id, { billing_status: "active" }, organization.name)}>
                {t("admin.organizations.reactivate")}
              </Button>
            )}
          </Space>
        ),
      },
    ];

    return (
      <Wrapper>
        {error && <Alert type="error" message={error} showIcon className="organizations-error" />}
        <Space className="toolbar">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => this.openEditor(null)}>
            {t("admin.organizations.new")}
          </Button>
        </Space>
        <Table rowKey="id" loading={loading} columns={columns} dataSource={rows} size="middle" pagination={false} />

        <Modal
          open={editing !== null}
          forceRender
          title={editing?.id ? t("admin.organizations.editTitle", { name: editing.name }) : t("admin.organizations.newTitle")}
          onOk={this.saveOrganization}
          confirmLoading={mutationLoading}
          onCancel={() => this.setState({ editing: null })}
          okText={t("admin.common.save")}
          cancelText={t("admin.common.cancel")}
        >
          <Form ref={this.formRef} layout="vertical">
            <Form.Item label={t("admin.common.name")} name="name" rules={[{ required: true, message: t("admin.organizations.required") }]}>
              <Input placeholder={t("admin.organizations.placeholders.name")} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.legalName")} name="legal_name">
              <Input placeholder={t("admin.organizations.placeholders.legalName")} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.plan")} name="billing_plan" tooltip={t("admin.organizations.planHelp")}>
              <Select options={["basic", "team", "enterprise", "custom"].map(value => ({value, label: t(`admin.organizations.plans.${value}`)}))}
                onChange={value => {
                  this.setState({selectedPlan: value});
                  if (PLAN_SEATS[value]) this.formRef.current?.setFieldValue("seat_limit", PLAN_SEATS[value]);
                  this.formRef.current?.setFieldValue("granted_features", []);
                }} />
            </Form.Item>
            <Space size="middle" className="form-row">
              <Form.Item label={t("admin.organizations.taxId")} name="tax_id" rules={[{ pattern: /^\d{9}$/, message: t("admin.organizations.taxIdValidation") }]}>
                <Input maxLength={9} className="tax-input" />
              </Form.Item>
              <Form.Item label={t("admin.organizations.seatLimit")} name="seat_limit" rules={[{ required: true, message: t("admin.organizations.required") }]}>
                <InputNumber min={1} max={500} disabled={selectedPlan !== "custom"} />
              </Form.Item>
              <Form.Item label={t("admin.organizations.billingStatus")} name="billing_status">
                <Select className="billing-select" options={Object.keys(STATUS_COLORS).map((value) => ({ value, label: t(`admin.common.statuses.${value}`) }))} />
              </Form.Item>
              <Form.Item label={t("admin.organizations.trialEnds")} name="trial_ends_on" tooltip={t("admin.organizations.trialEndsHelp")}>
                <Input type="date" className="date-input" />
              </Form.Item>
            </Space>
            <Form.Item label={t("admin.organizations.billingEmail")} name="billing_email" rules={[{ type: "email", message: t("admin.organizations.invalidEmail") }]}>
              <Input placeholder={t("admin.organizations.placeholders.billingEmail")} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.communicationLanguage")} name="locale" rules={[{ required: true, message: t("admin.organizations.required") }]}>
              <Select options={[
                { value: "el", label: t("admin.common.locales.el") },
                { value: "en", label: t("admin.common.locales.en") },
              ]} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.address")} name="address" rules={[{ required: true, message: t("admin.organizations.required") }]}>
              <Input placeholder={t("admin.organizations.placeholders.address")} />
            </Form.Item>
            <Space size="middle" className="form-row" align="start">
              <Form.Item label={t("admin.organizations.city")} name="city" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Input placeholder={t("admin.organizations.placeholders.city")} />
              </Form.Item>
              <Form.Item label={t("admin.organizations.country")} name="country_code" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t("admin.organizations.selectCountry")}
                  options={countryOptions(i18n.language)}
                  onChange={this.handleCountryChange}
                />
              </Form.Item>
            </Space>
            <Form.Item label={t("admin.organizations.timezone")} name="timezone" rules={[{ required: true, message: t("admin.organizations.timezoneRequiredForCountry") }]}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t("admin.organizations.selectTimezone")}
                options={timezoneOptions(selectedCountry)}
              />
            </Form.Item>
            <Form.Item label={t("admin.organizations.disabledFeatures")} name="disabled_features">
              <Checkbox.Group options={FEATURE_KEYS.filter(key => selectedPlan !== "custom" || key !== "intelligence").map((value) => ({ value, label: t(`admin.organizations.features.${value}`) }))} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.extraProducts")} name="granted_features" tooltip={t("admin.organizations.extraProductsHelp")}>
              <Checkbox.Group options={grants.map((value) => ({ value, label: t(`admin.organizations.features.${value}`) }))} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.notes")} name="notes"><Input.TextArea rows={2} /></Form.Item>
          </Form>
        </Modal>

        <Modal
          open={!!inviteOrg}
          title={t("admin.organizations.inviteTitle", { name: inviteOrg?.name })}
          onOk={this.sendInvite}
          confirmLoading={mutationLoading}
          onCancel={() => this.setState({ inviteOrg: null })}
          okText={t("admin.organizations.sendInvite")}
          cancelText={t("admin.common.cancel")}
        >
          <Form ref={this.inviteFormRef} layout="vertical" initialValues={{ role: "org_admin" }}>
            <Space size="middle" className="form-row">
              <Form.Item label={t("admin.organizations.firstName")} name="first_name" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Input placeholder={t("admin.organizations.placeholders.firstName")} autoComplete="given-name" />
              </Form.Item>
              <Form.Item label={t("admin.organizations.lastName")} name="last_name" rules={[{ required: true, message: t("admin.organizations.required") }]} className="flex-field">
                <Input placeholder={t("admin.organizations.placeholders.lastName")} autoComplete="family-name" />
              </Form.Item>
            </Space>
            <Form.Item label={t("admin.common.email")} name="email" rules={[{ required: true, type: "email", message: t("admin.organizations.invalidEmail") }]}>
              <Input placeholder={t("admin.organizations.placeholders.inviteEmail")} autoComplete="email" />
            </Form.Item>
            <Form.Item label={t("admin.common.role")} name="role">
              <Select options={[
                { value: "org_admin", label: t("admin.organizations.masterAccount") },
                { value: "member", label: t("admin.common.member") },
              ]} />
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={!!invoiceOrg}
          title={t("admin.organizations.issueInvoiceTitle", { name: invoiceOrg?.name })}
          onOk={this.issueInvoice}
          confirmLoading={mutationLoading}
          onCancel={() => this.setState({ invoiceOrg: null })}
          okText={t("admin.organizations.issueViaStripe")}
          cancelText={t("admin.common.cancel")}
        >
          <Form ref={this.invoiceFormRef} layout="vertical" initialValues={{ due_days: 30 }}>
            <Form.Item label={t("admin.organizations.amountEur")} name="amount_eur" rules={[{ required: true, message: t("admin.organizations.required") }]}>
              <InputNumber min={1} step={100} className="amount-input" />
            </Form.Item>
            <Form.Item label={t("admin.organizations.description")} name="description" rules={[{ required: true, message: t("admin.organizations.required") }]}>
              <Input placeholder={t("admin.organizations.placeholders.invoiceDescription")} />
            </Form.Item>
            <Form.Item label={t("admin.organizations.dueInDays")} name="due_days"><InputNumber min={7} max={90} /></Form.Item>
          </Form>
        </Modal>
      </Wrapper>
    );
  }
}

OrganizationsPanel.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    legal_name: PropTypes.string,
    tax_id: PropTypes.string,
    billing_email: PropTypes.string,
    locale: PropTypes.string.isRequired,
    address: PropTypes.string,
    city: PropTypes.string,
    country_code: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    seat_limit: PropTypes.number.isRequired,
    seats_taken: PropTypes.number.isRequired,
    billing_status: PropTypes.string.isRequired,
    trial_ends_on: PropTypes.string,
    invoice_due_on: PropTypes.string,
    notes: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    feature_flags: PropTypes.objectOf(PropTypes.bool).isRequired,
  })).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  mutationLoading: PropTypes.bool.isRequired,
  feedback: PropTypes.shape({
    type: PropTypes.string.isRequired,
    operation: PropTypes.string,
    message: PropTypes.string,
    messageKey: PropTypes.string,
    values: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    translatedValues: PropTypes.objectOf(PropTypes.string),
  }),
  fetchOrganizations: PropTypes.func.isRequired,
  createOrganization: PropTypes.func.isRequired,
  updateOrganization: PropTypes.func.isRequired,
  inviteOrganization: PropTypes.func.isRequired,
  issueInvoice: PropTypes.func.isRequired,
  clearFeedback: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string.isRequired }).isRequired,
  router: PropTypes.shape({ navigate: PropTypes.func.isRequired }).isRequired,
};

OrganizationsPanel.defaultProps = {
  error: null,
  feedback: null,
};

const mapStateToProps = ({ Admin }) => ({
  rows: Admin.organizations,
  loading: Admin.organizationsLoading,
  error: Admin.organizationsError,
  mutationLoading: Admin.mutationLoading,
  feedback: Admin.feedback,
});

const mapDispatchToProps = (dispatch) => ({
  fetchOrganizations: (params) => dispatch(fetchOrganizations(params)),
  createOrganization: (organization) => dispatch(createOrganization(organization)),
  updateOrganization: (id, organization, displayName) => dispatch(
    updateOrganization(id, organization, displayName),
  ),
  inviteOrganization: (id, invite, source) => dispatch(inviteOrganization(id, invite, source)),
  issueInvoice: (id, invoice) => dispatch(issueInvoice(id, invoice)),
  clearFeedback: () => dispatch(clearFeedback()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("common")(withRouter(OrganizationsPanel)));
